<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getLilithEffectRecovery,
  reconcileLilithEffect,
} from 'dashboard/api/lilithCustomTools';

const { accountId } = useAccount();
const { t } = useI18n();
const attempts = ref([]);
const offset = ref(0);
const loading = ref(false);
const saving = ref(false);
const evidence = ref({});
let loadVersion = 0;

const reportError = error =>
  useAlert(error?.response?.data?.error || t('TEAM_LILITH.EFFECT_RECOVERY.ERROR'));

const load = async (page = offset.value) => {
  const version = ++loadVersion;
  const account = accountId.value;
  loading.value = true;
  try {
    const response = await getLilithEffectRecovery(account, page);
    if (version !== loadVersion || account !== accountId.value) return;
    attempts.value = response.data?.payload || [];
    offset.value = page;
    evidence.value = {};
  } catch (error) {
    if (version === loadVersion) reportError(error);
  } finally {
    if (version === loadVersion) loading.value = false;
  }
};

const reconcile = async (attempt, outcome) => {
  const key = `${attempt.run_id}:${attempt.call_id}`;
  const note = (evidence.value[key] || '').trim();
  if (!note || attempt.active || saving.value || loading.value) return;
  const account = accountId.value;
  saving.value = true;
  try {
    await reconcileLilithEffect(account, attempt.run_id, attempt.call_id, {
      outcome,
      evidence: note,
    });
    if (account === accountId.value) await load();
  } catch (error) {
    if (account === accountId.value) reportError(error);
  } finally {
    saving.value = false;
  }
};

watch(accountId, () => {
  attempts.value = [];
  evidence.value = {};
  offset.value = 0;
  load(0);
}, { immediate: true });
</script>

<template>
  <section class="flex flex-col gap-3 rounded-xl border border-n-weak p-5">
    <div class="flex items-center justify-between gap-3">
      <h2 class="text-base font-semibold text-n-slate-12">
        {{ $t('TEAM_LILITH.EFFECT_RECOVERY.TITLE') }}
      </h2>
      <Button :label="$t('TEAM_LILITH.EFFECT_RECOVERY.REFRESH')" size="sm"
        variant="ghost" :disabled="loading || saving" @click="load()" />
    </div>
    <p class="text-sm text-n-slate-11">{{ $t('TEAM_LILITH.EFFECT_RECOVERY.DESCRIPTION') }}</p>
    <p v-if="loading" class="text-sm text-n-slate-10">{{ $t('TEAM_LILITH.EFFECT_RECOVERY.LOADING') }}</p>
    <p v-else-if="!attempts.length" class="text-sm text-n-slate-10">{{ $t('TEAM_LILITH.EFFECT_RECOVERY.EMPTY') }}</p>
    <article v-for="attempt in attempts" :key="`${attempt.run_id}:${attempt.call_id}`"
      class="flex flex-col gap-2 rounded-lg border border-n-weak p-4">
      <div class="text-sm font-medium text-n-slate-12">
        {{ attempt.effect }} · {{ $t('TEAM_LILITH.CONVERSATION', { id: attempt.conversation_ref }) }}
      </div>
      <p class="break-all text-xs text-n-slate-10">
        {{ attempt.run_id }} / {{ attempt.call_id }} · {{ attempt.requested_at || attempt.started_at }}
      </p>
      <pre v-if="attempt.arguments" class="whitespace-pre-wrap break-all text-xs text-n-slate-11">{{ JSON.stringify(attempt.arguments, null, 2) }}</pre>
      <p v-if="attempt.reconciliation" class="text-sm text-n-slate-11">
        {{ $t(`TEAM_LILITH.EFFECT_RECOVERY.${attempt.reconciliation.outcome === 'performed' ? 'PERFORMED' : 'NOT_PERFORMED'}`) }}:
        {{ attempt.reconciliation.evidence }} · {{ attempt.reconciliation.employee_ref }} · {{ attempt.reconciliation.recorded_at }}
      </p>
      <p v-else-if="attempt.active" class="text-sm text-n-slate-10">{{ $t('TEAM_LILITH.EFFECT_RECOVERY.ACTIVE') }}</p>
      <template v-else>
        <label class="text-sm text-n-slate-11">
          {{ $t('TEAM_LILITH.EFFECT_RECOVERY.EVIDENCE') }}
          <textarea v-model="evidence[`${attempt.run_id}:${attempt.call_id}`]" maxlength="1000"
            :disabled="saving || loading" rows="2"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 p-2" />
        </label>
        <div class="flex flex-wrap gap-2">
          <Button :label="$t('TEAM_LILITH.EFFECT_RECOVERY.PERFORMED')" size="sm"
            :disabled="saving || loading || !(evidence[`${attempt.run_id}:${attempt.call_id}`] || '').trim()"
            @click="reconcile(attempt, 'performed')" />
          <Button :label="$t('TEAM_LILITH.EFFECT_RECOVERY.NOT_PERFORMED')" size="sm" variant="outline"
            :disabled="saving || loading || !(evidence[`${attempt.run_id}:${attempt.call_id}`] || '').trim()"
            @click="reconcile(attempt, 'not_performed')" />
        </div>
      </template>
    </article>
    <div class="flex gap-2">
      <Button :label="$t('TEAM_LILITH.EFFECT_RECOVERY.PREVIOUS')" size="sm" variant="ghost"
        :disabled="offset === 0 || loading || saving" @click="load(Math.max(0, offset - 50))" />
      <Button :label="$t('TEAM_LILITH.EFFECT_RECOVERY.NEXT')" size="sm" variant="ghost"
        :disabled="attempts.length < 50 || loading || saving" @click="load(offset + 50)" />
    </div>
  </section>
</template>
